// Kai'Sa VFX:
//   KaisaDashVFX   — void-energy dash trail to landing position
//   KaisaRocketVFX — single purple rocket flying a unique bezier curve

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";
import type { UnitVisual } from "../UnitVisual";
import { KAISA_WINDUP_MS, KAISA_ROCKET_STAGGER_MS, KAISA_ROCKET_TRAVEL_MS } from "../../units/abilities/KaisaUlt";

// ── Bezier helpers ────────────────────────────────────────────────────────────

function bezierPoint(p0: Point, p1: Point, p2: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

// Tangent (derivative) of quadratic bezier — gives flight direction for rotation
function bezierTangent(p0: Point, p1: Point, p2: Point, t: number): Point {
  const mt = 1 - t;
  return {
    x: 2 * mt * (p1.x - p0.x) + 2 * t * (p2.x - p1.x),
    y: 2 * mt * (p1.y - p0.y) + 2 * t * (p2.y - p1.y),
  };
}

// Build a control point offset perpendicular to the from→to line by `amount` px
function controlPoint(from: Point, to: Point, amount: number): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  // perpendicular = (-dy, dx) normalized
  const px = -dy / len;
  const py =  dx / len;
  const midX = (from.x + to.x) / 2;
  const midY = (from.y + to.y) / 2;
  return { x: midX + px * amount, y: midY + py * amount };
}

// ── Dash VFX ─────────────────────────────────────────────────────────────────

const KAISA_DASH_MS   = 480;
const DASH_WINDUP_MS  = 60;
const DASH_TRAVEL_MS  = KAISA_DASH_MS - DASH_WINDUP_MS - 120;
const DASH_LAND_MS    = 120;

type TrailDot = { x: number; y: number; age: number; hue: number };

export class KaisaDashVFX implements Animation {
  private elapsed = 0;
  private trail: TrailDot[] = [];
  onEnd?: () => void;

  constructor(
    private visual: UnitVisual,
    private startPx: Point,
    private endPx: Point,
    private hexSize: number,
  ) {}

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, KAISA_DASH_MS);

    const dashT = Math.max(0, (this.elapsed - DASH_WINDUP_MS) / DASH_TRAVEL_MS);
    if (dashT > 0 && dashT < 1) {
      const eased = Easing.easeInOutCubic(Math.min(dashT, 1));
      this.visual.x = this.startPx.x + (this.endPx.x - this.startPx.x) * eased;
      this.visual.y = this.startPx.y + (this.endPx.y - this.startPx.y) * eased;

      if (Math.random() < 0.65) {
        this.trail.push({
          x: this.visual.x + (Math.random() - 0.5) * 10,
          y: this.visual.y + (Math.random() - 0.5) * 10,
          age: 0,
          hue: 260 + Math.random() * 60,
        });
      }
    }

    for (const d of this.trail) d.age += dt;
    this.trail = this.trail.filter((d) => d.age < 260);

    if (this.elapsed >= KAISA_DASH_MS && this.onEnd) {
      this.onEnd();
      this.onEnd = undefined;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    for (const d of this.trail) {
      const t = d.age / 260;
      const r = 5 * (1 - t);
      ctx.globalAlpha = (1 - t) * 0.75;
      ctx.shadowColor = `hsl(${d.hue}, 100%, 70%)`;
      ctx.shadowBlur = 10;
      ctx.fillStyle = t < 0.35 ? "#b06aff" : "#6a00dd";
      ctx.beginPath();
      ctx.arc(d.x, d.y, Math.max(r, 0.5), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const landStart = DASH_WINDUP_MS + DASH_TRAVEL_MS;
    if (this.elapsed >= landStart) {
      const landT = (this.elapsed - landStart) / DASH_LAND_MS;
      const outerR = this.hexSize * 1.8 * Easing.easeOutCubic(landT);
      ctx.save();
      ctx.globalAlpha = (1 - landT) * 0.9;
      ctx.strokeStyle = "#9d4edd";
      ctx.lineWidth = 3 * (1 - landT) + 1;
      ctx.shadowColor = "#c77dff";
      ctx.shadowBlur = 22;
      ctx.beginPath();
      ctx.arc(this.endPx.x, this.endPx.y, outerR, 0, Math.PI * 2);
      ctx.stroke();

      const flashR = this.hexSize * 0.45 * (1 - Easing.easeInQuad(landT));
      if (flashR > 0) {
        ctx.globalAlpha = (1 - landT) * 0.95;
        ctx.fillStyle = "#e0aaff";
        ctx.shadowBlur = 16;
        ctx.beginPath();
        ctx.arc(this.endPx.x, this.endPx.y, flashR, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
  }

  isDone(): boolean {
    return this.elapsed >= KAISA_DASH_MS;
  }
}

// ── Rocket VFX (one per projectile, unique bezier curve) ─────────────────────

const ROCKET_IMPACT_MS = 180;
const ROCKET_TOTAL = KAISA_ROCKET_TRAVEL_MS + ROCKET_IMPACT_MS;

// Per-rocket curve amount assignments — spread, alternate sides, vary arc depth
// Index 0-3 for each salvo of 4.
const CURVE_AMOUNTS = [
  -55,   // hard left
   30,   // moderate right
  -18,   // gentle left
   65,   // hard right
];

type ImpactSpark = { angle: number; speed: number; size: number };

export class KaisaRocketVFX implements Animation {
  private elapsed = 0;
  private fired = false;
  private readonly totalMs: number;
  private ctrl: Point | null = null;  // computed at launch moment
  private to: Point | null = null;    // snapshotted at launch moment
  private sparks: ImpactSpark[];

  constructor(
    private from: Point,
    private getTo: () => Point,   // live position — snapshotted when rocket launches
    private startDelay: number,
    private curveAmount: number,
    private onLand?: () => void,
  ) {
    this.totalMs = startDelay + ROCKET_TOTAL;
    this.sparks = Array.from({ length: 7 }, () => ({
      angle: Math.random() * Math.PI * 2,
      speed: 35 + Math.random() * 50,
      size: 2 + Math.random() * 2.5,
    }));
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, this.totalMs);

    // Snapshot target position the moment this rocket starts flying
    if (this.to === null && this.elapsed >= this.startDelay) {
      this.to = this.getTo();
      this.ctrl = controlPoint(this.from, this.to, this.curveAmount);
    }

    const travelEnd = this.startDelay + KAISA_ROCKET_TRAVEL_MS;
    if (!this.fired && this.elapsed >= travelEnd) {
      this.fired = true;
      this.onLand?.();
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.elapsed <= this.startDelay || this.to === null || this.ctrl === null) return;
    const phase = this.elapsed - this.startDelay;

    if (phase < KAISA_ROCKET_TRAVEL_MS) {
      this.drawTravel(ctx, phase / KAISA_ROCKET_TRAVEL_MS);
    } else {
      this.drawImpact(ctx, (phase - KAISA_ROCKET_TRAVEL_MS) / ROCKET_IMPACT_MS);
    }
  }

  private drawTravel(ctx: CanvasRenderingContext2D, t: number): void {
    const to = this.to!;
    const ctrl = this.ctrl!;
    const eased = Easing.easeInOutCubic(t);
    const pos = bezierPoint(this.from, ctrl, to, eased);

    // Tangent for rotation (use slightly ahead t to stay stable near endpoints)
    const tTan = Math.min(eased + 0.01, 1);
    const tan = bezierTangent(this.from, ctrl, to, tTan);
    const angle = Math.atan2(tan.y, tan.x);

    // Curved smoke trail — draw bezier path up to current t
    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = "#c77dff";
    ctx.lineWidth = 2.5;
    ctx.shadowColor = "#9d4edd";
    ctx.shadowBlur = 8;
    ctx.beginPath();
    const trailSteps = 12;
    for (let i = 0; i <= trailSteps; i++) {
      const st = Easing.easeInOutCubic((i / trailSteps) * eased);
      const p = bezierPoint(this.from, ctrl, to, st);
      i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.restore();

    // Rocket body
    ctx.save();
    ctx.translate(pos.x, pos.y);
    ctx.rotate(angle);
    ctx.shadowColor = "#e0aaff";
    ctx.shadowBlur = 18;

    // Engine glow
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = "#ff80ff";
    ctx.beginPath();
    ctx.ellipse(-9, 0, 5, 2.5, 0, 0, Math.PI * 2);
    ctx.fill();

    // Body
    ctx.globalAlpha = 1;
    ctx.fillStyle = "#9d4edd";
    ctx.beginPath();
    ctx.moveTo(12, 0);
    ctx.lineTo(-8, -3.5);
    ctx.lineTo(-8, 3.5);
    ctx.closePath();
    ctx.fill();

    // Nose highlight
    ctx.fillStyle = "#e0aaff";
    ctx.beginPath();
    ctx.moveTo(12, 0);
    ctx.lineTo(0, -1.5);
    ctx.lineTo(0, 1.5);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  private drawImpact(ctx: CanvasRenderingContext2D, t: number): void {
    const to = this.to!;
    const fade = 1 - Easing.easeInQuad(t);
    const ringR = 8 + Easing.easeOutQuad(t) * 20;

    ctx.save();
    ctx.globalAlpha = fade * 0.85;
    ctx.strokeStyle = "#c77dff";
    ctx.lineWidth = 2;
    ctx.shadowColor = "#9d4edd";
    ctx.shadowBlur = 18;
    ctx.beginPath();
    ctx.arc(to.x, to.y, ringR, 0, Math.PI * 2);
    ctx.stroke();

    for (const p of this.sparks) {
      const dist = p.speed * (t * ROCKET_IMPACT_MS / 1000);
      ctx.globalAlpha = fade * 0.8;
      ctx.fillStyle = "#e0aaff";
      ctx.shadowBlur = 6;
      ctx.beginPath();
      ctx.arc(
        to.x + Math.cos(p.angle) * dist,
        to.y + Math.sin(p.angle) * dist,
        p.size * (1 - t * 0.6),
        0, Math.PI * 2,
      );
      ctx.fill();
    }
    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= this.totalMs;
  }
}

// ── Salvo helper — fires `rocketsCount` rockets at one target ────────────────

export function launchRocketSalvo(
  queue: { pushFlourish(a: Animation): void },
  fromPx: Point,
  getTo: () => Point,   // live position getter — snapshotted per-rocket at launch
  salvoDelay: number,
  rocketsCount: number,
  onLastLand: () => void,
): void {
  for (let i = 0; i < rocketsCount; i++) {
    const startDelay = salvoDelay + i * KAISA_ROCKET_STAGGER_MS;
    const curveAmount = CURVE_AMOUNTS[i % CURVE_AMOUNTS.length];
    const isLast = i === rocketsCount - 1;
    queue.pushFlourish(
      new KaisaRocketVFX(fromPx, getTo, startDelay, curveAmount, isLast ? onLastLand : undefined),
    );
  }
}
